import React, { useState, useMemo } from 'react';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TableSortLabel,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Clear as ClearIcon,
  OpenInNew as OpenInNewIcon,
  Widgets as ComponentsIcon,
  SwapHoriz as TransferIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { componentsService } from '../services/components.service';
import { ComponentFormWizard } from '../components/ComponentForm/ComponentFormWizard';
import { TransferComponentDialog } from '../components/TransferComponentDialog';
import { useSpace } from '../contexts/SpaceContext';
import { useSecurityAssistant } from '../contexts/SecurityAssistantContext';
import type { Component, CreateComponentDto } from '@risk-management/shared';

type SortField =
  | 'name'
  | 'type'
  | 'environment'
  | 'confidentiality'
  | 'integrity'
  | 'availability'
  | 'scope';
type SortDirection = 'asc' | 'desc';

export const ComponentList: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { currentSpace } = useSpace();
  const { setComponentContext } = useSecurityAssistant();
  const [formOpen, setFormOpen] = useState(false);
  const [editingComponent, setEditingComponent] = useState<
    Component | undefined
  >();
  const [selectedComponentId, setSelectedComponentId] = useState<string | null>(
    null
  );
  const [transferDialogOpen, setTransferDialogOpen] = useState(false);
  const [transferringComponent, setTransferringComponent] =
    useState<Component | null>(null);

  // Filter states
  const [searchText, setSearchText] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [environmentFilter, setEnvironmentFilter] = useState<string>('');
  const [scopeFilter, setScopeFilter] = useState<string>('space');

  // Sort states
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  // Fetch components for the current space
  const {
    data: components = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ['components', currentSpace?.id],
    queryFn: () =>
      currentSpace?.id
        ? componentsService.getBySpace(currentSpace.id, true)
        : Promise.resolve([]),
    enabled: !!currentSpace?.id,
  });

  // Get unique values for filters
  const filterOptions = useMemo(() => {
    const types = [...new Set(components.map(c => c.type))].sort();
    const environments = [...new Set(components.map(c => c.environment))]
      .filter(Boolean)
      .sort();
    return { types, environments };
  }, [components]);

  // Filter and sort components
  const filteredAndSortedComponents = useMemo(() => {
    let result = [...components];

    // Apply text search filter
    if (searchText) {
      const searchLower = searchText.toLowerCase();
      result = result.filter(
        c =>
          c.name.toLowerCase().includes(searchLower) ||
          c.type.toLowerCase().includes(searchLower) ||
          c.description?.toLowerCase().includes(searchLower)
      );
    }

    // Apply type filter
    if (typeFilter) {
      result = result.filter(c => c.type === typeFilter);
    }

    // Apply environment filter
    if (environmentFilter) {
      result = result.filter(c => c.environment === environmentFilter);
    }

    // Apply scope filter
    if (scopeFilter) {
      if (scopeFilter === 'global') {
        // Show only global components (from any space)
        result = result.filter(c => c.isGlobal);
      } else if (scopeFilter === 'space') {
        // Show only components owned by current space (both private and global)
        result = result.filter(c => c.spaceId === currentSpace?.id);
      }
    }

    // Apply sorting
    result.sort((a, b) => {
      let comparison = 0;
      switch (sortField) {
        case 'name':
          comparison = a.name.localeCompare(b.name);
          break;
        case 'type':
          comparison = a.type.localeCompare(b.type);
          break;
        case 'environment':
          comparison = (a.environment || '').localeCompare(b.environment || '');
          break;
        case 'confidentiality':
          comparison =
            (a.confidentialityScore || 0) - (b.confidentialityScore || 0);
          break;
        case 'integrity':
          comparison = (a.integrityScore || 0) - (b.integrityScore || 0);
          break;
        case 'availability':
          comparison = (a.availabilityScore || 0) - (b.availabilityScore || 0);
          break;
        case 'scope':
          comparison = (a.isGlobal ? 1 : 0) - (b.isGlobal ? 1 : 0);
          break;
      }
      return sortDirection === 'asc' ? comparison : -comparison;
    });

    return result;
  }, [
    components,
    searchText,
    typeFilter,
    environmentFilter,
    scopeFilter,
    sortField,
    sortDirection,
    currentSpace?.id,
  ]);

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (data: CreateComponentDto) => componentsService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['components'], exact: false });
      setFormOpen(false);
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: CreateComponentDto }) =>
      componentsService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['components'], exact: false });
      setFormOpen(false);
      setEditingComponent(undefined);
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => componentsService.deleteComponent(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['components'], exact: false });
    },
  });

  // Transfer mutation
  const transferMutation = useMutation({
    mutationFn: ({
      id,
      targetSpaceId,
    }: {
      id: string;
      targetSpaceId: string;
    }) => componentsService.transferToSpace(id, targetSpaceId),
    onSuccess: () => {
      // Invalidate all component queries (prefix match)
      queryClient.invalidateQueries({ queryKey: ['components'], exact: false });
      setTransferDialogOpen(false);
      setTransferringComponent(null);
    },
  });

  const handleCreate = () => {
    setEditingComponent(undefined);
    setFormOpen(true);
  };

  const handleEdit = (e: React.MouseEvent, component: Component) => {
    e.stopPropagation(); // Prevent row click navigation
    setEditingComponent(component);
    setFormOpen(true);
  };

  const handleDeleteClick = (e: React.MouseEvent, id: string, name: string) => {
    e.stopPropagation(); // Prevent row click navigation
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if ((globalThis as any).confirm(t('component.deleteConfirm', { name }))) {
      deleteMutation.mutate(id);
    }
  };

  const handleTransferClick = (e: React.MouseEvent, component: Component) => {
    e.stopPropagation(); // Prevent row click navigation
    setTransferringComponent(component);
    setTransferDialogOpen(true);
  };

  const handleTransfer = async (targetSpaceId: string) => {
    if (!transferringComponent) return;
    await transferMutation.mutateAsync({
      id: transferringComponent.id,
      targetSpaceId,
    });
  };

  const handleRowClick = (component: Component) => {
    // Set context for security assistant sidebar
    setSelectedComponentId(component.id);
    setComponentContext({
      componentId: component.id,
      componentName: component.name,
      componentType: component.type,
      confidentialityScore: component.confidentialityScore,
      integrityScore: component.integrityScore,
      availabilityScore: component.availabilityScore,
      dataCategories: component.dataCategories || [],
      environment: component.environment,
      spaceId: component.spaceId,
    });
  };

  const handleRowDoubleClick = (componentId: string) => {
    navigate(`/components/${componentId}`);
  };

  const handleOpenDetail = (e: React.MouseEvent, componentId: string) => {
    e.stopPropagation();
    navigate(`/components/${componentId}`);
  };

  const handleSubmit = async (data: CreateComponentDto) => {
    if (editingComponent) {
      await updateMutation.mutateAsync({ id: editingComponent.id, data });
    } else {
      await createMutation.mutateAsync(data);
    }
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const clearFilters = () => {
    setSearchText('');
    setTypeFilter('');
    setEnvironmentFilter('');
    setScopeFilter('');
  };

  const hasActiveFilters =
    searchText || typeFilter || environmentFilter || scopeFilter;

  const getScoreColor = (score: number): 'success' | 'warning' | 'error' => {
    if (score <= 3.0) return 'success';
    if (score <= 7.0) return 'warning';
    return 'error';
  };

  const formatScore = (score?: number | string): string => {
    if (score === undefined || score === null) return '-';
    const num = typeof score === 'number' ? score : parseFloat(String(score));
    return isNaN(num) ? '-' : num.toFixed(1);
  };

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        {t('component.error.loading')}: {(error as Error).message}
      </Alert>
    );
  }

  return (
    <Box sx={{ maxWidth: 1400, mx: 'auto', p: 3 }}>
      {/* Header */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <ComponentsIcon sx={{ fontSize: 32, color: 'primary.main' }} />
          <Typography variant="h4">{t('component.title')}</Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreate}
          disabled={!currentSpace}
        >
          {t('component.create')}
        </Button>
      </Box>

      {!currentSpace && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {t('space.selectSpace')}
        </Alert>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
          {/* Search */}
          <TextField
            size="small"
            placeholder={t('common.search')}
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            sx={{ minWidth: 250 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: searchText && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearchText('')}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          {/* Type Filter */}
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>{t('component.type')}</InputLabel>
            <Select
              value={typeFilter}
              label={t('component.type')}
              onChange={e => setTypeFilter(e.target.value)}
            >
              <MenuItem value="">{t('common.all')}</MenuItem>
              {filterOptions.types.map(type => (
                <MenuItem key={type} value={type}>
                  {t(`componentTypes:${type}`)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Environment Filter */}
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>{t('component.environment')}</InputLabel>
            <Select
              value={environmentFilter}
              label={t('component.environment')}
              onChange={e => setEnvironmentFilter(e.target.value)}
            >
              <MenuItem value="">{t('common.all')}</MenuItem>
              {filterOptions.environments.map(env => (
                <MenuItem key={env} value={env}>
                  {t(`component.environments.${env}`)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Scope Filter */}
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>{t('component.scope')}</InputLabel>
            <Select
              value={scopeFilter}
              label={t('component.scope')}
              onChange={e => setScopeFilter(e.target.value)}
            >
              <MenuItem value="">{t('common.all')}</MenuItem>
              <MenuItem value="global">{t('component.global')}</MenuItem>
              <MenuItem value="space">{t('component.spaceOwned')}</MenuItem>
            </Select>
          </FormControl>

          {/* Clear Filters */}
          {hasActiveFilters && (
            <Button
              size="small"
              startIcon={<FilterListIcon />}
              onClick={clearFilters}
            >
              {t('common.clearFilters')}
            </Button>
          )}

          {/* Results count */}
          <Box sx={{ ml: 'auto' }}>
            <Typography variant="body2" color="text.secondary">
              {t('common.showingResults', {
                count: filteredAndSortedComponents.length,
                total: components.length,
              })}
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={sortField === 'name'}
                  direction={sortField === 'name' ? sortDirection : 'asc'}
                  onClick={() => handleSort('name')}
                >
                  {t('component.name')}
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortField === 'type'}
                  direction={sortField === 'type' ? sortDirection : 'asc'}
                  onClick={() => handleSort('type')}
                >
                  {t('component.type')}
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortField === 'environment'}
                  direction={
                    sortField === 'environment' ? sortDirection : 'asc'
                  }
                  onClick={() => handleSort('environment')}
                >
                  {t('component.environment')}
                </TableSortLabel>
              </TableCell>
              <TableCell align="center">
                <Box display="flex" gap={1} justifyContent="center">
                  <TableSortLabel
                    active={sortField === 'confidentiality'}
                    direction={
                      sortField === 'confidentiality' ? sortDirection : 'asc'
                    }
                    onClick={() => handleSort('confidentiality')}
                  >
                    C
                  </TableSortLabel>
                  <TableSortLabel
                    active={sortField === 'integrity'}
                    direction={
                      sortField === 'integrity' ? sortDirection : 'asc'
                    }
                    onClick={() => handleSort('integrity')}
                  >
                    I
                  </TableSortLabel>
                  <TableSortLabel
                    active={sortField === 'availability'}
                    direction={
                      sortField === 'availability' ? sortDirection : 'asc'
                    }
                    onClick={() => handleSort('availability')}
                  >
                    A
                  </TableSortLabel>
                </Box>
              </TableCell>
              <TableCell align="center">
                <TableSortLabel
                  active={sortField === 'scope'}
                  direction={sortField === 'scope' ? sortDirection : 'asc'}
                  onClick={() => handleSort('scope')}
                >
                  {t('component.scope')}
                </TableSortLabel>
              </TableCell>
              <TableCell align="center">{t('common.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredAndSortedComponents.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography variant="body2" color="text.secondary">
                    {hasActiveFilters
                      ? t('component.noMatchingResults')
                      : t('component.empty')}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredAndSortedComponents.map(component => {
                const canEdit = component.spaceId === currentSpace?.id;
                const cScore =
                  typeof component.confidentialityScore === 'number'
                    ? component.confidentialityScore
                    : parseFloat(String(component.confidentialityScore)) || 0;
                const iScore =
                  typeof component.integrityScore === 'number'
                    ? component.integrityScore
                    : parseFloat(String(component.integrityScore)) || 0;
                const aScore =
                  typeof component.availabilityScore === 'number'
                    ? component.availabilityScore
                    : parseFloat(String(component.availabilityScore)) || 0;

                return (
                  <TableRow
                    key={component.id}
                    hover
                    selected={selectedComponentId === component.id}
                    sx={{ cursor: 'pointer' }}
                    onClick={() => handleRowClick(component)}
                    onDoubleClick={() => handleRowDoubleClick(component.id)}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {component.name}
                      </Typography>
                      {component.description && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          noWrap
                          sx={{ maxWidth: 300, display: 'block' }}
                        >
                          {component.description}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {t(`componentTypes:${component.type}`, component.type)}
                    </TableCell>
                    <TableCell>
                      {component.environment && (
                        <Chip
                          label={t(
                            `component.environments.${component.environment}`
                          )}
                          size="small"
                          sx={
                            component.environment === 'production'
                              ? {
                                  backgroundColor: '#5a5a5a',
                                  color: '#ffffff',
                                }
                              : undefined
                          }
                        />
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Box display="flex" gap={0.5} justifyContent="center">
                        <Tooltip
                          title={t('classification.confidentiality.title')}
                        >
                          <Chip
                            label={`C:${formatScore(component.confidentialityScore)}`}
                            size="small"
                            color={getScoreColor(cScore)}
                          />
                        </Tooltip>
                        <Tooltip title={t('classification.integrity.title')}>
                          <Chip
                            label={`I:${formatScore(component.integrityScore)}`}
                            size="small"
                            color={getScoreColor(iScore)}
                          />
                        </Tooltip>
                        <Tooltip title={t('classification.availability.title')}>
                          <Chip
                            label={`A:${formatScore(component.availabilityScore)}`}
                            size="small"
                            color={getScoreColor(aScore)}
                          />
                        </Tooltip>
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      {component.isGlobal ? (
                        <Chip
                          label={t('component.global')}
                          size="small"
                          color="primary"
                        />
                      ) : (
                        <Chip label={t('component.team')} size="small" />
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title={t('common.openDetail')}>
                        <IconButton
                          size="small"
                          onClick={e => handleOpenDetail(e, component.id)}
                        >
                          <OpenInNewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip
                        title={
                          !canEdit
                            ? t('component.error.notOwner')
                            : t('common.edit')
                        }
                      >
                        <span>
                          <IconButton
                            size="small"
                            onClick={e => handleEdit(e, component)}
                            disabled={!canEdit}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip
                        title={
                          !canEdit
                            ? t('component.error.notOwner')
                            : t('component.transfer.action')
                        }
                      >
                        <span>
                          <IconButton
                            size="small"
                            onClick={e => handleTransferClick(e, component)}
                            disabled={!canEdit}
                          >
                            <TransferIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip
                        title={
                          !canEdit
                            ? t('component.error.notOwner')
                            : t('common.delete')
                        }
                      >
                        <span>
                          <IconButton
                            size="small"
                            onClick={e =>
                              handleDeleteClick(e, component.id, component.name)
                            }
                            disabled={!canEdit}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <ComponentFormWizard
        open={formOpen}
        onClose={() => {
          setFormOpen(false);
          setEditingComponent(undefined);
        }}
        onSubmit={handleSubmit}
        component={editingComponent}
        currentSpaceId={currentSpace?.id || ''}
      />

      <TransferComponentDialog
        open={transferDialogOpen}
        onClose={() => {
          setTransferDialogOpen(false);
          setTransferringComponent(null);
        }}
        onTransfer={handleTransfer}
        componentName={transferringComponent?.name || ''}
        currentSpaceId={transferringComponent?.spaceId || ''}
        isLoading={transferMutation.isPending}
      />
    </Box>
  );
};
